import type { HolidayInputs, RunReport, RunRow } from './api';

export const defaults: HolidayInputs = {
  holiday_date: '2026-10-12',
  code: '#-THANKSGIVING',
  country: 'CA',
  states: '',
  school_ids: '',
  no_reflow_websites: 'tdsb.on.ca,ocdsb.ca',
  no_reflow_school_ids: '',
  no_reflow: false,
};

export const codes = [
  { value: '#-THANKSGIVING', label: 'Thanksgiving' },
  { value: '#-WINTERBREAK', label: 'Winter break' },
  { value: '#-SPRINGBREAK', label: 'Spring break' },
  { value: '#-BREAK', label: 'Break' },
  { value: 'X', label: 'Day off' },
];

export const dateValue = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const result = new Date(year, month - 1, day);
  return result.getFullYear() === year &&
    result.getMonth() === month - 1 &&
    result.getDate() === day
    ? result
    : null;
};

export const validInputs = (inputs: HolidayInputs) =>
  Boolean(dateValue(inputs.holiday_date)) &&
  /^[a-z]{2}$/i.test(inputs.country.trim()) &&
  codes.some(({ value }) => value === inputs.code);

export interface Review {
  id: string;
  inputs: HolidayInputs;
  environment: string;
  expires: number;
}

export function canApply(
  review: Review | null,
  report: RunReport | undefined,
  inputs: HolidayInputs,
  environment: string,
  now = Date.now(),
) {
  return Boolean(
    review &&
      report &&
      review.id === report.run_id &&
      report.status === 'done' &&
      report.params?.dry_run === true &&
      report.environment === environment &&
      review.environment === environment &&
      review.expires > now &&
      JSON.stringify(review.inputs) === JSON.stringify(inputs),
  );
}

export const columns: Array<[keyof RunRow, string]> = [
  ['school_id', 'School'],
  ['state', 'State'],
  ['action', 'Action'],
  ['displaced_code', 'Displaced code'],
  ['reflow', 'Re-flow'],
  ['reflow_reason', 'Re-flow reason'],
  ['reflow_start', 'Re-flow start'],
  ['reflow_end', 'Re-flow end'],
  ['termination_reason', 'Termination reason'],
  ['reflow_rows_changed', 'Re-flow rows'],
];

export function reportCsv(rows: RunRow[]) {
  const cell = (value: unknown) => {
    const text = String(value ?? '');
    const safe = /^[=+@\-\t\r\n]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  return [
    columns.map(([, label]) => cell(label)).join(','),
    ...rows.map((row) => columns.map(([key]) => cell(row[key])).join(',')),
  ].join('\r\n');
}
